import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useMerchantsListQuery } from '~platform/entities/merchant';
import { useGetPointsOfSalesQuery } from '~platform/entities/pos';

import { ampli } from '~shared/analytics';

import {
  getDefaultDashboardFilter,
  persistDashboardFilters,
  restoreDashboardFilters,
  type DashboardFiltersValue,
} from './dashboard-filters';

const MIN_MERCHANTS_TO_FILTER = 2;

export function useDashboardFilters() {
  const methods = useForm<DashboardFiltersValue>({
    defaultValues: getDefaultDashboardFilter(),
  });

  const [appliedFilters, setAppliedFilters] = useState<DashboardFiltersValue>();

  const {
    data: merchantsList,
    isError: isMerchantsError,
    isSuccess: isMerchantsSuccess,
  } = useMerchantsListQuery();
  const { data: posList, isSuccess: isPosSuccess } = useGetPointsOfSalesQuery();

  const trackFilterApplied = useCallback((filters: DashboardFiltersValue) => {
    const { startDate, endDate } = filters.period ?? {};

    ampli.dashboardScreenFilterApplied({
      filter_period: startDate && endDate ? `${startDate}-${endDate}` : '',
      filter_chosenMerchantsCount: filters.merchantIds.length,
      filter_chosenStoresCount: filters.posIds.length,
    });
  }, []);

  const applyFilters = useCallback(() => {
    const filters = methods.getValues();
    setAppliedFilters(filters);
    persistDashboardFilters(filters);
    trackFilterApplied(filters);
  }, [methods, trackFilterApplied]);

  const resetFilters = useCallback(() => {
    const defaults = getDefaultDashboardFilter();
    methods.reset(defaults);
    setAppliedFilters(defaults);
    persistDashboardFilters(defaults);
    trackFilterApplied(defaults);
  }, [methods, trackFilterApplied]);

  const clearSelectFilters = useCallback(() => {
    methods.setValue('merchantIds', []);
    methods.setValue('posIds', []);
  }, [methods]);

  const activeFiltersCount =
    (appliedFilters?.merchantIds.length ? 1 : 0) +
    (appliedFilters?.posIds.length ? 1 : 0);

  const isHydratedRef = useRef(false);
  useEffect(() => {
    if (isHydratedRef.current || !isMerchantsSuccess || !isPosSuccess) {
      return;
    }

    isHydratedRef.current = true;
    const restored = restoreDashboardFilters(merchantsList, posList);
    methods.reset(restored);
    setAppliedFilters(restored);
    persistDashboardFilters(restored);
    trackFilterApplied(restored);
  }, [
    isMerchantsSuccess,
    isPosSuccess,
    merchantsList,
    posList,
    trackFilterApplied,
  ]);

  useEffect(() => {
    if (isMerchantsError || isMerchantsSuccess) {
      ampli.dashboardScreenShown({ merchantsCount: merchantsList?.length });
    }
  }, [isMerchantsError, isMerchantsSuccess, merchantsList]);

  const hideMerchantFilter =
    !isMerchantsSuccess || merchantsList.length < MIN_MERCHANTS_TO_FILTER;

  return {
    methods,
    appliedFilters,
    applyFilters,
    resetFilters,
    clearSelectFilters,
    activeFiltersCount,
    hideMerchantFilter,
  };
}
